import { useEffect, useRef, useState } from 'react';
import {
  type ProviderAuthDto,
  useGetVehiclesEnrollmentViewMutation,
  type VehiclesEnrollmentViewItemDto,
} from '~/api/generated-api';
import { useOrgId } from '~/common/hooks/useOrgId';

import type { EnrollmentStatus } from '~/features/integrations/manage/enrollmentTable/enrollmentTable.constants';

export type EnrollmentViewRowsParams = {
  providerCode: ProviderAuthDto['providerCode'];
  enrollmentStatuses: EnrollmentStatus[];
  fleetIds: string[];
  /** Already trimmed. Blank sends no search term. */
  searchTerm: string;
  pageIndex: number;
  pageSize: number;
};

export type EnrollmentViewRows = {
  rows: VehiclesEnrollmentViewItemDto[];
  total: number;
  status: 'loading' | 'ready' | 'error';
  /** Refetches the current page after a failed load. */
  retry: () => void;
};

type LoadedPage = Omit<EnrollmentViewRows, 'retry'> & { pageKey: string };

/**
 * Reads one page of the enrollment view. The API pages by continuation token only, so reaching
 * page N walks every preceding page whose token is not cached yet. `enrollmentStatuses` and
 * `fleetIds` must keep their identity between renders, or every render refetches.
 */
export const useEnrollmentViewRows = ({
  providerCode,
  enrollmentStatuses,
  fleetIds,
  searchTerm,
  pageIndex,
  pageSize,
}: EnrollmentViewRowsParams): EnrollmentViewRows => {
  const organizationId = useOrgId();
  const [getVehiclesEnrollmentView] = useGetVehiclesEnrollmentViewMutation();
  const [retryCount, setRetryCount] = useState(0);
  const tokenCacheKey = JSON.stringify([
    organizationId,
    providerCode,
    enrollmentStatuses,
    [...fleetIds].sort(),
    searchTerm,
    pageSize,
  ]);
  const pageKey = `${tokenCacheKey}:${pageIndex}:${retryCount}`;
  // Only the current query's tokens are kept; changing any input starts over from page 0.
  const pageTokensRef = useRef<{
    key: string;
    tokens: Map<number, string | undefined>;
  }>({ key: '', tokens: new Map() });
  const [loadedPage, setLoadedPage] = useState<LoadedPage>({
    pageKey: '',
    rows: [],
    total: 0,
    status: 'loading',
  });

  useEffect(() => {
    if (!organizationId) return;
    let isCurrentRequest = true;
    setLoadedPage({ pageKey, rows: [], total: 0, status: 'loading' });
    if (pageTokensRef.current.key !== tokenCacheKey) {
      pageTokensRef.current = {
        key: tokenCacheKey,
        tokens: new Map([[0, undefined]]),
      };
    }
    const pageTokens = pageTokensRef.current.tokens;

    const fetchPage = async (index: number) => {
      const continuationToken = pageTokens.get(index);
      if (index > 0 && !continuationToken) {
        throw new Error('Enrollment view page token is unavailable');
      }
      const response = await getVehiclesEnrollmentView({
        'cs-organization': organizationId,
        ...(continuationToken
          ? { 'cs-continuation-token': continuationToken }
          : {}),
        providerCode,
        vehiclesEnrollmentViewRequestDto: {
          enrollmentStatuses,
          fleetIds,
          vins: [],
          search: searchTerm ? { term: searchTerm } : {},
          size: pageSize,
        },
      }).unwrap();
      if (!response.success || !response.result) {
        throw new Error('Unable to load the enrollment view');
      }
      // A page without a token stays uncached, so retrying refetches it instead of skipping it.
      if (response.result.continuation_token) {
        pageTokens.set(index + 1, response.result.continuation_token);
      }
      return response.result;
    };

    void (async () => {
      try {
        for (let preceding = 0; preceding < pageIndex; preceding += 1) {
          if (pageTokens.has(preceding + 1)) continue;
          const previous = await fetchPage(preceding);
          if (!isCurrentRequest) return;
          if (!pageTokens.get(preceding + 1) || !previous.items.length) {
            throw new Error('Enrollment view page is unavailable');
          }
        }
        const result = await fetchPage(pageIndex);
        if (!isCurrentRequest) return;
        setLoadedPage({
          pageKey,
          rows: result.items,
          total: result.total_items ?? result.items.length,
          status: 'ready',
        });
      } catch {
        if (!isCurrentRequest) return;
        setLoadedPage({ pageKey, rows: [], total: 0, status: 'error' });
      }
    })();

    return () => {
      isCurrentRequest = false;
    };
  }, [
    enrollmentStatuses,
    fleetIds,
    getVehiclesEnrollmentView,
    organizationId,
    pageIndex,
    pageKey,
    pageSize,
    providerCode,
    searchTerm,
    tokenCacheKey,
  ]);

  const isCurrentPage = loadedPage.pageKey === pageKey;
  return {
    rows: isCurrentPage ? loadedPage.rows : [],
    total: isCurrentPage ? loadedPage.total : 0,
    status: isCurrentPage ? loadedPage.status : 'loading',
    retry: () => setRetryCount((count) => count + 1),
  };
};
